<ngx-spinner type="ball-atom"></ngx-spinner>
<div class="component-container">
  <div class="component-header">
    <div class="component-header-title">
      <h2>Automatic Correction</h2>
      <p class="component-header-subtitle">
        Data-quality checks across the database. Inspect findings, export them as CSV, or apply automated corrections where available.
      </p>
    </div>
    <button
      mat-stroked-button
      color="primary"
      type="button"
      [disabled]="refreshingAll"
      (click)="loadChecks()">
      <mat-icon>refresh</mat-icon>
      Refresh all
    </button>
  </div>

  @if (loaded) {
    <div class="summary-bar">
      <div class="summary-item">
        <span class="summary-label">Total checks</span>
        <span class="summary-value">{{ checks.length }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Issues found</span>
        <span class="summary-value" [class.has-issues]="totalIssues() > 0">{{ totalIssues() }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Clean checks</span>
        <span class="summary-value">{{ cleanChecks() }}</span>
      </div>
    </div>
  }

  <div class="checks-list">
    @for (row of checks; track trackByCheckType($index, row)) {
      <div class="check-card"
           [class.is-expanded]="isExpanded(row.checkType)"
           [class.has-error]="!!row.error">
        <div class="check-row">
          <div class="check-main">
            @if (row.error) {
              <span class="count-chip" data-severity="error" matTooltip="Count query failed">
                <mat-icon>error_outline</mat-icon>
              </span>
            } @else {
              <span class="count-chip" [attr.data-severity]="countSeverity(row.count)">
                {{ row.count }}
              </span>
            }
            <div class="check-label">
              <span class="label-text">{{ row.label }}</span>
              <div class="label-flags">
                @if (row.hasCorrection) {
                  <span class="flag flag-auto" matTooltip="This check exposes an automated correction">
                    <mat-icon>auto_fix_high</mat-icon>
                    Auto-fix
                  </span>
                }
                @if (row.isDestructive) {
                  <span class="flag flag-destructive" matTooltip="Applying this correction deletes records">
                    <mat-icon>delete_forever</mat-icon>
                    Destructive
                  </span>
                }
                @if (row.error) {
                  <span class="flag flag-error" [matTooltip]="row.error">
                    <mat-icon>error_outline</mat-icon>
                    Count failed
                  </span>
                }
              </div>
            </div>
          </div>

          <div class="check-actions">
            <button
              mat-icon-button
              type="button"
              [disabled]="row.refreshing"
              [matTooltip]="row.refreshing ? 'Refreshing…' : 'Recount'"
              (click)="refreshOne(row)">
              <mat-icon [class.spin]="row.refreshing">refresh</mat-icon>
            </button>

            <button
              mat-stroked-button
              type="button"
              [disabled]="row.count === 0 || !!row.error"
              (click)="toggleDetails(row)">
              <mat-icon>{{ isExpanded(row.checkType) ? 'expand_less' : 'expand_more' }}</mat-icon>
              Details
            </button>

            <button
              mat-stroked-button
              type="button"
              [disabled]="row.count === 0 || row.exporting || !!row.error"
              (click)="exportCsv(row)">
              <mat-icon>file_download</mat-icon>
              CSV
            </button>

            <div class="apply-slot">
              @if (row.hasCorrection) {
                <button
                  mat-raised-button
                  [color]="row.isDestructive ? 'warn' : 'primary'"
                  type="button"
                  [disabled]="row.count === 0 || row.applying || !!row.error"
                  (click)="applyCorrection(row)">
                  <mat-icon>{{ row.isDestructive ? 'delete_forever' : 'auto_fix_high' }}</mat-icon>
                  {{ row.isDestructive ? 'Delete' : 'Apply' }}
                </button>
              }
            </div>
          </div>
        </div>

        @if (isExpanded(row.checkType)) {
          @let state = details[row.checkType];
          <div class="check-details">
            @if (state?.loading) {
              <div class="details-loading">Loading…</div>
            } @else if (!state || state.results.length === 0) {
              <div class="empty-results">No matching records found.</div>
            } @else {
              <table mat-table [dataSource]="state.results" [trackBy]="trackByRow" class="results-table">
                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef>Name</th>
                  <td mat-cell *matCellDef="let r">
                    @if (r.name) {
                      <span class="link-cell">
                        @if (r.directorId) {
                          <a [routerLink]="['/directors/edit', r.directorId]"
                             target="_blank"
                             rel="noopener"
                             class="row-link"
                             title="Open director">
                            <mat-icon>open_in_new</mat-icon>
                          </a>
                        }
                        {{ r.name }}
                      </span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="company">
                  <th mat-header-cell *matHeaderCellDef>Company</th>
                  <td mat-cell *matCellDef="let r">
                    @if (r.company) {
                      <span class="link-cell">
                        @if (r.companyId) {
                          <a [routerLink]="['/companies/edit', r.companyId]"
                             [queryParams]="companyLinkQueryParams(row.checkType, r)"
                             target="_blank"
                             rel="noopener"
                             class="row-link"
                             title="Open company">
                            <mat-icon>open_in_new</mat-icon>
                          </a>
                        }
                        {{ r.company }}
                      </span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="country">
                  <th mat-header-cell *matHeaderCellDef>Country</th>
                  <td mat-cell *matCellDef="let r">{{ r.country }}</td>
                </ng-container>

                <ng-container matColumnDef="year">
                  <th mat-header-cell *matHeaderCellDef>Year</th>
                  <td mat-cell *matCellDef="let r">{{ r.year }}</td>
                </ng-container>

                <ng-container matColumnDef="info">
                  <th mat-header-cell *matHeaderCellDef>Info</th>
                  <td mat-cell *matCellDef="let r">{{ r.info }}</td>
                </ng-container>

                <ng-container matColumnDef="rowActions">
                  <th mat-header-cell *matHeaderCellDef class="row-actions-cell"></th>
                  <td mat-cell *matCellDef="let r" class="row-actions-cell">
                    <button
                      mat-icon-button
                      color="warn"
                      type="button"
                      [disabled]="deletingRows.has(r.directorId)"
                      matTooltip="Delete this record"
                      (click)="deleteRow(row, r)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="detailColumnsFor(row)"></tr>
                <tr mat-row *matRowDef="let r; columns: detailColumnsFor(row)"></tr>
              </table>

              <mat-paginator
                [length]="state.totalCount"
                [pageSize]="state.pageSize"
                [pageSizeOptions]="pageSizeOptions"
                [pageIndex]="state.page - 1"
                (page)="onDetailsPage(row.checkType, $event)">
              </mat-paginator>
            }
          </div>
        }
      </div>
    } @empty {
      @if (loaded) {
        <div class="empty-state">No checks are registered.</div>
      }
    }
  </div>
</div>
